import React, { useState, useRef, useCallback, useEffect } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { Iconify } from "../Minimal/src/components/iconify/iconify";
import NumericKeypad from "./NumericKeypad";
import NumericKeypadQuickOrder from "./NumericKeypadQuickOrder";

const inlineQtyDraftByKey = new Map();
const QTY_MOVE_TO_SEARCH_ATTR = "data-qo-qty-to-search";

const markQtyMoveToSearch = () => {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute(QTY_MOVE_TO_SEARCH_ATTR, "1");
  window.setTimeout(() => {
    document.documentElement.removeAttribute(QTY_MOVE_TO_SEARCH_ATTR);
  }, 500);
};

const isQtyMoveToSearchLocked = () =>
  typeof document !== "undefined" &&
  document.documentElement.hasAttribute(QTY_MOVE_TO_SEARCH_ATTR);

const QuantityControl = ({
  isEditMode = false,
  value = 1,
  onChange,
  onIncrement,
  onDecrement,
  min = 1,
  max = 9999,
  disabled = false,
  size = "medium",
  autoOpen = false,
  autoFocusInput = false,
  keepFocusOnEnter = false,
  preferInlineQty = false,
  inlineEditKey = "",
  onPopupClose = null,
  onPopupOpen = null,
  isQuickOrder = false,
  orderType = "Box",
  canSplit = false,
  onOrderTypeChange = null,
  onAddAsNewType = null,
  allowDecimal = false,
  isPromoTypeFree = false,
  promoAlternateQtyButtons = false,
  onPromoPButtonClick = null,
  onPromoCPButtonClick = null
}) => {
  const qtyBlocked = disabled || isPromoTypeFree;
  const showPromoAltSideButtons = promoAlternateQtyButtons && qtyBlocked;
  const pOpensPromo = Boolean(showPromoAltSideButtons && typeof onPromoPButtonClick === "function");
  const cpOpensPromo = Boolean(showPromoAltSideButtons && typeof onPromoCPButtonClick === "function");
  const promoQtySideActionsActive = pOpensPromo || cpOpensPromo;
  const [openKeypad, setOpenKeypad] = useState(false);
  const [keypadError, setKeypadError] = useState("");
  const [inlineEditing, setInlineEditing] = useState(false);
  const [inlineValue, setInlineValue] = useState("");

  const longPressTimer = useRef(null);
  const longPressInterval = useRef(null);
  const longPressActive = useRef(false);
  const wasLongPress = useRef(false);
  const valueRef = useRef(value);
  const hasAutoOpened = useRef(false);
  const hasAutoFocusedInput = useRef(false);
  const autoOpenRef = useRef(false);
  const autoFocusInputRef = useRef(autoFocusInput);
  const isProcessingRef = useRef(false);
  const clickDebounceTimerRef = useRef(null);
  const longPressStartValueRef = useRef(0);
  const longPressAccumulatedRef = useRef(0);
  const inlineInputRef = useRef(null);
  const inlineEditingRef = useRef(false);
  const inlineValueRef = useRef("");
  const inlineEditKeyRef = useRef(String(inlineEditKey || ""));
  const blurCommitTimerRef = useRef(null);
  const [optimisticValue, setOptimisticValue] = useState(null);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    autoFocusInputRef.current = autoFocusInput;
  }, [autoFocusInput]);

  useEffect(() => {
    inlineEditKeyRef.current = String(inlineEditKey || "");
  }, [inlineEditKey]);

  useEffect(() => {
    if (optimisticValue === null) return;
    if (Number(value) !== Number(optimisticValue)) return;
    setOptimisticValue(null);
    if (inlineEditingRef.current) {
      const synced = String(value);
      inlineValueRef.current = synced;
      setInlineValue(synced);
      const key = inlineEditKeyRef.current;
      if (key) inlineQtyDraftByKey.set(key, synced);
    }
  }, [value, optimisticValue]);

  useEffect(() => {
    if (optimisticValue !== null) return;
    if (!inlineEditingRef.current) return;
    const synced = String(value ?? "");
    if (inlineValueRef.current === synced) return;
    const key = inlineEditKeyRef.current;
    const draft = key ? inlineQtyDraftByKey.get(key) : undefined;
    if (draft != null && String(draft) !== synced && document.activeElement === inlineInputRef.current) {
      return;
    }
    inlineValueRef.current = synced;
    setInlineValue(synced);
    if (key) inlineQtyDraftByKey.set(key, synced);
  }, [value, optimisticValue]);

  useEffect(() => {
    if (!autoOpen || autoFocusInput) {
      hasAutoOpened.current = false;
      autoOpenRef.current = false;
      return;
    }
    if (autoOpen && !hasAutoOpened.current && !disabled && !openKeypad && !inlineEditing) {
      hasAutoOpened.current = true;
      autoOpenRef.current = true;
      setOpenKeypad(true);
      setKeypadError("");
      if (onPopupOpen) {
        setTimeout(() => {
          onPopupOpen();
        }, 0);
      }
    }
  }, [autoOpen, autoFocusInput, qtyBlocked, openKeypad, onPopupOpen, inlineEditing, disabled]);

  useEffect(() => {
    if (!autoFocusInput) {
      hasAutoFocusedInput.current = false;
      return;
    }
    if (qtyBlocked || openKeypad) {
      return;
    }

    const key = String(inlineEditKey || "");
    const draft = key ? inlineQtyDraftByKey.get(key) : undefined;
    const initial = draft != null ? String(draft) : String(valueRef.current ?? value ?? "");
    const alreadyEditing = inlineEditingRef.current;

    if (!hasAutoFocusedInput.current || !alreadyEditing) {
      hasAutoFocusedInput.current = true;
      inlineValueRef.current = initial;
      setInlineValue(initial);
      inlineEditingRef.current = true;
      setInlineEditing(true);
    }

    const shouldSelect = draft == null || String(draft) === String(valueRef.current ?? value ?? "");
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (isQtyMoveToSearchLocked()) return;
        const el = inlineInputRef.current;
        if (!el) return;
        if (document.activeElement === el) return;
        el.focus();
        if (shouldSelect) {
          el.select();
        } else {
          const len = String(el.value || "").length;
          try {
            el.setSelectionRange(len, len);
          } catch (_) {}
        }
      }, 0);
    });
  }, [autoFocusInput, qtyBlocked, openKeypad, inlineEditKey]);

  useEffect(() => {
    return () => {
      if (clickDebounceTimerRef.current) {
        clearTimeout(clickDebounceTimerRef.current);
        clickDebounceTimerRef.current = null;
      }
      if (blurCommitTimerRef.current) {
        clearTimeout(blurCommitTimerRef.current);
        blurCommitTimerRef.current = null;
      }
      isProcessingRef.current = false;
    };
  }, []);

  const buttonSize = size === "small" ? 25 : 35;
  const fontSize = size === "small" ? "0.75rem" : "0.875rem";

  const keypadFormatMax = allowDecimal ? 9999.99 : 9999;

  const applyQuantityStep = useCallback(
    (delta) => {
      if (qtyBlocked) return false;

      const current =
        optimisticValue !== null ? optimisticValue : valueRef.current;
      const next = Math.min(max, Math.max(min, current + delta));
      if (next === current) {
        if (delta > 0 && onIncrement) {
          onIncrement(1);
        } else if (delta < 0 && onDecrement) {
          onDecrement(-1);
        }
        return false;
      }

      setOptimisticValue(next);
      valueRef.current = next;
      if (inlineEditingRef.current) {
        const nextStr = String(next);
        inlineValueRef.current = nextStr;
        setInlineValue(nextStr);
        const key = inlineEditKeyRef.current;
        if (key) inlineQtyDraftByKey.set(key, nextStr);
      }

      let accepted = true;
      if (delta > 0) {
        if (onIncrement) {
          accepted = onIncrement(1) !== false;
        } else {
          onChange(next);
        }
      } else if (onDecrement) {
        accepted = onDecrement(-1) !== false;
      } else {
        onChange(next);
      }

      if (!accepted) {
        setOptimisticValue(null);
        valueRef.current = value;
        if (inlineEditingRef.current) {
          const fallback = String(value ?? "");
          inlineValueRef.current = fallback;
          setInlineValue(fallback);
          const key = inlineEditKeyRef.current;
          if (key) inlineQtyDraftByKey.set(key, fallback);
        }
        return false;
      }

      return true;
    },
    [qtyBlocked, optimisticValue, min, max, onChange, onIncrement, onDecrement, value]
  );

  const incrementValue = useCallback(() => {
    applyQuantityStep(1);
  }, [applyQuantityStep]);

  const decrementValue = useCallback(() => {
    applyQuantityStep(-1);
  }, [applyQuantityStep]);

  const handleIncrement = (e) => {
    if (wasLongPress.current) {
      wasLongPress.current = false;
      return;
    }
    applyQuantityStep(1);
  };

  const handleDecrement = (e) => {
    if (wasLongPress.current) {
      wasLongPress.current = false;
      return;
    }
    applyQuantityStep(-1);
  };

  const applyLongPressFinal = () => {
    if (!longPressActive.current || longPressAccumulatedRef.current === 0) return;
    const finalValue = Math.min(max, Math.max(min, longPressStartValueRef.current + longPressAccumulatedRef.current));
    onChange(finalValue);
    longPressAccumulatedRef.current = 0;
  };

  const clearTimers = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    if (longPressInterval.current) {
      clearInterval(longPressInterval.current);
      longPressInterval.current = null;
    }
    if (clickDebounceTimerRef.current) {
      clearTimeout(clickDebounceTimerRef.current);
      clickDebounceTimerRef.current = null;
    }
    if (longPressActive.current) {
      applyLongPressFinal();
    }
    longPressActive.current = false;
    isProcessingRef.current = false;
  };

  const startLongPress = (direction) => {
    if (qtyBlocked) return;
    wasLongPress.current = false;
    clearTimers();
    longPressStartValueRef.current = valueRef.current;
    longPressAccumulatedRef.current = 0;

    longPressTimer.current = setTimeout(() => {
      longPressActive.current = true;
      wasLongPress.current = true;
      const step = direction === "increment" ? 1 : -1;
      longPressAccumulatedRef.current += step;
      const next = Math.min(max, Math.max(min, longPressStartValueRef.current + longPressAccumulatedRef.current));
      setOptimisticValue(next);

      longPressInterval.current = setInterval(() => {
        longPressAccumulatedRef.current += step;
        const nextVal = Math.min(max, Math.max(min, longPressStartValueRef.current + longPressAccumulatedRef.current));
        setOptimisticValue(nextVal);
        if (nextVal === min || nextVal === max) {
          clearTimers();
        }
      }, 100);
    }, 500);
  };

  const handleMouseDown = (direction) => (e) => {
    startLongPress(direction);
  };

  const handleMouseUp = (e) => {
    if (e) {
      e.preventDefault();
    }
    clearTimers();
  };

  const handleTouchStart = (direction) => (e) => {
    startLongPress(direction);
  };

  const handleTouchEnd = (e) => {
    if (e) {
      e.preventDefault();
    }
    clearTimers();
  };

  const startInlineEdit = () => {
    if (qtyBlocked) return;
    const key = String(inlineEditKeyRef.current || "");
    const draft = key ? inlineQtyDraftByKey.get(key) : undefined;
    const initial = draft != null ? String(draft) : String(valueRef.current ?? value ?? "");
    inlineValueRef.current = initial;
    setInlineValue(initial);
    inlineEditingRef.current = true;
    setInlineEditing(true);
    requestAnimationFrame(() => {
      setTimeout(() => {
        const el = inlineInputRef.current;
        if (!el) return;
        el.focus();
        el.select();
      }, 0);
    });
  };

  const handleOpenKeypad = () => {
    if (inlineEditing) return;
    if (qtyBlocked) return;
    if (keepFocusOnEnter || preferInlineQty) {
      startInlineEdit();
      return;
    }
    setOpenKeypad(true);
    setKeypadError("");
  };

  const handleCloseKeypad = () => {
    setOpenKeypad(false);
    setKeypadError("");
    hasAutoOpened.current = true;
    autoOpenRef.current = false;
    if (onPopupClose) {
      markQtyMoveToSearch();
      onPopupClose();
    }
  };

  const notifyMoveToSearch = useCallback(() => {
    markQtyMoveToSearch();
    if (onPopupClose) {
      onPopupClose();
    }
  }, [onPopupClose]);

  const handleQtyEnterMoveToSearch = (event) => {
    if (event.key !== "Enter" || keepFocusOnEnter) return;
    event.preventDefault();
    event.stopPropagation();
    notifyMoveToSearch();
  };

  const commitInlineQuantity = useCallback(
    ({ moveToSearch = false, stayEditing = false } = {}) => {
      if (!inlineEditingRef.current) {
        if (moveToSearch && !stayEditing) {
          notifyMoveToSearch();
        }
        return;
      }
      if (!stayEditing) {
        inlineEditingRef.current = false;
      }
      const key = inlineEditKeyRef.current;
      if (moveToSearch && !stayEditing) {
        notifyMoveToSearch();
      }
      const finish = () => {
        if (key && !stayEditing) {
          inlineQtyDraftByKey.delete(key);
        }
        if (!stayEditing) {
          setInlineEditing(false);
          if (!moveToSearch && onPopupOpen) {
            onPopupOpen();
          }
        } else {
          const refocus = () => {
            const editKey = inlineEditKeyRef.current;
            const el =
              inlineInputRef.current ||
              (editKey
                ? document.querySelector(`input[data-inline-qty-key="${editKey}"]`)
                : null);
            if (!el || typeof el.focus !== "function") return;
            el.focus();
            if (typeof el.select === "function") {
              el.select();
            }
          };
          requestAnimationFrame(() => {
            setTimeout(refocus, 0);
            setTimeout(refocus, 80);
          });
        }
      };
      if (qtyBlocked) {
        finish();
        return;
      }
      const raw = String(inlineValueRef.current ?? "").trim();
      const numValue = allowDecimal ? parseFloat(raw) : parseInt(raw, 10);
      if (raw === "" || Number.isNaN(numValue) || numValue < min) {
        const fallback = String(valueRef.current ?? value ?? "");
        inlineValueRef.current = fallback;
        setInlineValue(fallback);
        finish();
        return;
      }
      const next = allowDecimal
        ? Math.round(numValue * 100) / 100
        : Math.round(numValue);
      const current = Number(valueRef.current);
      if (next !== current) {
        const accepted = onChange(next) !== false;
        if (!accepted) {
          const fallback = String(valueRef.current ?? value ?? "");
          inlineValueRef.current = fallback;
          setInlineValue(fallback);
          finish();
          return;
        }
      }
      finish();
    },
    [qtyBlocked, allowDecimal, min, onChange, onPopupOpen, notifyMoveToSearch, value]
  );

  const handleInlineChange = (event) => {
    const next = event.target.value;
    const apply = (val) => {
      inlineValueRef.current = val;
      setInlineValue(val);
      const key = inlineEditKeyRef.current;
      if (key) {
        inlineQtyDraftByKey.set(key, val);
      }
    };
    if (allowDecimal) {
      if (next === "" || /^\d*\.?\d{0,2}$/.test(next)) {
        apply(next);
      }
      return;
    }
    if (next === "" || /^\d*$/.test(next)) {
      apply(next);
    }
  };

  const handleInlineBlur = () => {
    if (blurCommitTimerRef.current) {
      clearTimeout(blurCommitTimerRef.current);
    }
    blurCommitTimerRef.current = setTimeout(() => {
      blurCommitTimerRef.current = null;
      if (!inlineEditingRef.current) {
        return;
      }
      if (inlineInputRef.current && document.activeElement === inlineInputRef.current) {
        return;
      }
      const key = inlineEditKeyRef.current;
      const activeInline = key
        ? document.querySelector(`input[data-inline-qty-key="${key}"]`)
        : null;
      if (activeInline && document.activeElement === activeInline) {
        return;
      }
      if (activeInline && autoFocusInputRef.current) {
        return;
      }
      if (keepFocusOnEnter) {
        const qtyScope = document.activeElement instanceof Element
          ? document.activeElement.closest("[data-cart-qty-scope]")
          : null;
        if (qtyScope) {
          return;
        }
      }
      commitInlineQuantity({ moveToSearch: false });
    }, 120);
  };

  const handleInlineKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      if (blurCommitTimerRef.current) {
        clearTimeout(blurCommitTimerRef.current);
        blurCommitTimerRef.current = null;
      }
      commitInlineQuantity({
        moveToSearch: !keepFocusOnEnter,
        stayEditing: keepFocusOnEnter,
      });
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      if (blurCommitTimerRef.current) {
        clearTimeout(blurCommitTimerRef.current);
        blurCommitTimerRef.current = null;
      }
      const key = inlineEditKeyRef.current;
      if (key) {
        inlineQtyDraftByKey.delete(key);
      }
      inlineEditingRef.current = false;
      const fallback = String(valueRef.current ?? value ?? "");
      inlineValueRef.current = fallback;
      setInlineValue(fallback);
      setInlineEditing(false);
      if (onPopupOpen) {
        onPopupOpen();
      }
    }
  };

  const handleKeypadConfirm = (newValue, newOrderType = null) => {
    const numValue = parseFloat(newValue);

    if (isNaN(numValue) || numValue < min || numValue > keypadFormatMax) {
      setKeypadError(`Please enter a valid quantity (${min}-${keypadFormatMax})`);
      return;
    }

    if (canSplit && newOrderType && newOrderType !== orderType) {
      if (onAddAsNewType) {
        onAddAsNewType(newOrderType, numValue);
        setOpenKeypad(false);
        setKeypadError("");
        hasAutoOpened.current = true;
        autoOpenRef.current = false;
        if (!keepFocusOnEnter) {
          notifyMoveToSearch();
        }
        return;
      }
      if (onOrderTypeChange) {
        onOrderTypeChange(newOrderType);
      }
    }

    const accepted = onChange(numValue) !== false;
    if (!accepted) {
      return;
    }

    setOpenKeypad(false);
    setKeypadError("");
    hasAutoOpened.current = true;
    autoOpenRef.current = false;
    if (!keepFocusOnEnter) {
      notifyMoveToSearch();
    }
  };

  const displayValue = optimisticValue !== null ? optimisticValue : value;
  const formattedDisplayValue =
    allowDecimal && displayValue !== "" && displayValue != null && Number.isFinite(Number(displayValue))
      ? Number(displayValue).toFixed(2)
      : displayValue;
  const isMinimum = Number(displayValue) <= min;
  const isMaximum = Number(displayValue) >= max;
  const qtyValueDisabledUx = qtyBlocked && promoQtySideActionsActive;
  const qtyMiddleTabEnabled = !qtyValueDisabledUx && !qtyBlocked;

  const decInteractDisabled = qtyBlocked;
  const incInteractDisabled = qtyBlocked;
  const decAtMinVisual = !pOpensPromo && isMinimum && !qtyBlocked;
  const incAtLimitVisual = !cpOpensPromo && isMaximum && !qtyBlocked;

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.5,
          opacity:
            qtyBlocked && !promoQtySideActionsActive ? 1 : 1,
          pointerEvents:
            promoQtySideActionsActive ? "auto" : qtyBlocked ? "none" : "auto"
        }}
      >
        <span style={pOpensPromo ? { pointerEvents: "auto" } : undefined}>
          <IconButton
            {...(pOpensPromo
              ? {
                onClick: (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onPromoPButtonClick();
                }
              }
              : {
                onClick: handleDecrement,
                onMouseDown: handleMouseDown("decrement"),
                onMouseUp: handleMouseUp,
                onMouseLeave: handleMouseUp,
                onTouchStart: handleTouchStart("decrement"),
                onTouchEnd: handleTouchEnd,
                onKeyDown: handleQtyEnterMoveToSearch
              })}
            disabled={
              !pOpensPromo && decInteractDisabled
            }
            size="small"
            sx={{
              width: showPromoAltSideButtons ? "auto" : buttonSize,
              height: buttonSize,
              minWidth: showPromoAltSideButtons ? (size === "small" ? 28 : 36) : buttonSize,
              minHeight: buttonSize,
              px: showPromoAltSideButtons ? 0.75 : 0,
              border: "1px solid",
              borderColor: pOpensPromo
                ? "grey.600"
                : isMinimum || qtyBlocked
                  ? "grey.300"
                  : "grey.400",
              borderRadius: 1,
              opacity: !pOpensPromo && (decInteractDisabled || decAtMinVisual) ? 1 : 1,
              backgroundColor: pOpensPromo ? "grey.100" : "background.paper",
              boxShadow: pOpensPromo ? 2 : !(decInteractDisabled || decAtMinVisual) ? 1 : "none",
              transition: "all 0.2s ease-in-out",
              ...(pOpensPromo
                ? {
                  "&:hover": {
                    backgroundColor: "grey.200",
                    boxShadow: 3,
                    transform: "scale(1.02)"
                  }
                }
                : {
                  "&:hover": {
                    backgroundColor:
                      decInteractDisabled || decAtMinVisual ? "background.paper" : "grey.100",
                    transform: decInteractDisabled || decAtMinVisual ? "scale(1.05)" : "scale(1.05)",
                    boxShadow: decInteractDisabled || decAtMinVisual ? 2 : 2
                  }
                }),
              "&:active": {
                transform: "scale(0.95)",
                boxShadow: 0
              },
              // "&:focus-visible": {
              //   outline: "1px solid",
              //   outlineColor: "primary",
              //   outlineOffset: 1
              // }
            }}
          >
            {showPromoAltSideButtons ? (
              <Typography
                component="span"
                sx={{
                  fontSize: size === "small" ? "0.7rem" : "0.8rem",
                  fontWeight: 800,
                  lineHeight: 1,
                  color: "text.primary"
                }}
              >
                P
              </Typography>
            ) : (
              <Iconify icon="ic:round-minus" width={18} />
            )}
          </IconButton>
        </span>

        <Box
          tabIndex={qtyMiddleTabEnabled && !inlineEditing ? 0 : -1}
          role={qtyMiddleTabEnabled && !inlineEditing ? "button" : undefined}
          onKeyDown={(e) => {
            if (!qtyMiddleTabEnabled || inlineEditing) return;
            if (e.key === "Enter") {
              e.preventDefault();
              if (!keepFocusOnEnter && isQuickOrder) {
                notifyMoveToSearch();
                return;
              }
              handleOpenKeypad();
              return;
            }
            if (e.key === " ") {
              e.preventDefault();
              handleOpenKeypad();
            }
          }}
          onClick={inlineEditing ? undefined : handleOpenKeypad}
          sx={{
            minWidth: 45,
            height: buttonSize,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: qtyValueDisabledUx ? "not-allowed" : inlineEditing ? "text" : "pointer",
            border: "1px solid",
            borderColor: qtyValueDisabledUx ? "#000" : "#000",
            borderRadius: 1,
            backgroundColor:
              qtyValueDisabledUx ? "action.hover" : "background.paper",
            px: inlineEditing ? 0.25 : 1,
            opacity: qtyValueDisabledUx ? 1 : 1,
            transition: "all 0.2s ease-in-out",
            pointerEvents: qtyValueDisabledUx ? "none" : "auto",
            ...(!qtyValueDisabledUx && !qtyBlocked && !inlineEditing
              ? {
                "&:hover": {
                  backgroundColor: "grey.50",
                  borderColor: "#F8991D",
                  boxShadow: 1
                },
                "&:active": {
                  transform: "scale(0.98)"
                }
              }
              : {}),
            ...(qtyMiddleTabEnabled && !inlineEditing
              ? {
                "&:focus-visible": {
                  outline: "none",
                  borderColor: "primary.main",
                  boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}40`
                }
              }
              : {})
          }}
        >
          {inlineEditing ? (
            <input
              ref={inlineInputRef}
              value={inlineValue}
              onChange={handleInlineChange}
              onBlur={handleInlineBlur}
              onKeyDown={handleInlineKeyDown}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              disabled={qtyBlocked}
              aria-label="Quantity"
              inputMode={allowDecimal ? "decimal" : "numeric"}
              data-inline-qty-key={String(inlineEditKey || "")}
              style={{
                width: 40,
                height: buttonSize - 4,
                padding: 0,
                margin: 0,
                border: "none",
                outline: "none",
                background: "transparent",
                textAlign: "center",
                fontSize,
                fontWeight: isEditMode ? 600 : 500,
                fontFamily: "inherit",
                color: "inherit",
              }}
            />
          ) : (
            <Typography
              variant="body2"
              fontWeight={isEditMode ? 600 : 500}
              sx={{
                fontSize,
                userSelect: "none",
                color:
                  qtyValueDisabledUx ? "text.primary" : "text.primary"
              }}
            >
              {formattedDisplayValue}
            </Typography>
          )}
        </Box>

        <span style={cpOpensPromo ? { pointerEvents: "auto" } : undefined}>
          <IconButton
            {...(cpOpensPromo
              ? {
                onClick: (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onPromoCPButtonClick();
                }
              }
              : {
                onClick: handleIncrement,
                onMouseDown: handleMouseDown("increment"),
                onMouseUp: handleMouseUp,
                onMouseLeave: handleMouseUp,
                onTouchStart: handleTouchStart("increment"),
                onTouchEnd: handleTouchEnd,
                onKeyDown: handleQtyEnterMoveToSearch
              })}
            disabled={
              !cpOpensPromo && incInteractDisabled
            }
            size="small"
            sx={{
              width: showPromoAltSideButtons ? "auto" : buttonSize,
              height: buttonSize,
              minWidth: showPromoAltSideButtons ? (size === "small" ? 34 : 42) : buttonSize,
              minHeight: buttonSize,
              px: showPromoAltSideButtons ? 0.5 : 0,
              border: "1px solid",
              borderColor: cpOpensPromo
                ? "#F8991D"
                : isMaximum || qtyBlocked
                  ? "#F8991D"
                  : "#F8991D",
              borderRadius: 1,
              opacity: !cpOpensPromo && (incInteractDisabled || incAtLimitVisual) ? 1 : 1,
              backgroundColor:
                cpOpensPromo ? "grey.200" : "background.paper",
              boxShadow: cpOpensPromo ? 2 : !(incInteractDisabled || incAtLimitVisual) ? 1 : 1,
              transition: "all 0.2s ease-in-out",

              color: cpOpensPromo
                ? "#F8991D"
                : isMaximum || qtyBlocked
                  ? "#F8991D"
                  : "#F8991D",
              ...(cpOpensPromo
                ? {
                  "&:hover": {
                    backgroundColor: "grey.200",
                    boxShadow: 3,
                    transform: "scale(1.02)",
                    color: "#F8991D"
                  }
                }
                : {
                  "&:hover": {
                    backgroundColor: incInteractDisabled || incAtLimitVisual ? "F8991D" : "#F8991D",
                    transform: incInteractDisabled || incAtLimitVisual ? "scale(1.05)" : "scale(1.05)",
                    boxShadow: incInteractDisabled || incAtLimitVisual ? 2 : 2,
                    color: incInteractDisabled || incAtLimitVisual ? "#F8991D" : "#fff"
                  }
                }),
              "&:active": {
                transform: "scale(0.95)",
                boxShadow: 0
              },
              // "&:focus-visible": {
              //   outline: "1px solid",
              //   outlineColor: "primary",
              //   outlineOffset: 1
              // }
            }}
          >
            {showPromoAltSideButtons ? (
              <Typography
                component="span"
                sx={{
                  fontSize: size === "small" ? "0.65rem" : "0.75rem",
                  fontWeight: 800,
                  lineHeight: 1,
                  color: "inherit"
                }}
              >
                CP
              </Typography>
            ) : (
              <Iconify icon="ic:round-plus" width={18} />
            )}
          </IconButton>
        </span>
      </Box>

      {openKeypad ? (
        isQuickOrder ? (
        <NumericKeypadQuickOrder
          open={openKeypad}
          onClose={handleCloseKeypad}
          onConfirm={handleKeypadConfirm}
          initialValue={value}
          title="Enter Quantity"
          error={keypadError}
          min={min}
          max={keypadFormatMax}
          canSplit={canSplit}
          initialOrderType={orderType}
          onOrderTypeChange={onOrderTypeChange}
          allowDecimal={allowDecimal}
        />
        ) : (
        <NumericKeypad
          open={openKeypad}
          onClose={handleCloseKeypad}
          onConfirm={handleKeypadConfirm}
          initialValue={value}
          title="Enter Quantity"
          error={keypadError}
          min={min}
          max={keypadFormatMax}
          canSplit={canSplit}
          initialOrderType={orderType}
          onOrderTypeChange={onOrderTypeChange}
          allowDecimal={allowDecimal}
        />
        )
      ) : null}
    </>
  );
};

export default QuantityControl;

